import { useCallback, useEffect, useState } from "react";
import { apiFetch, ApiError } from "./api";
import type { RecStatus } from "./database.types";

// M5 — the "Sugerencias de la semana" ledger. Reads the site's real
// recommendations, generates fresh ones, transitions status (accept → pending,
// reject → rejected) and downloads each rec's paste-ready file bundle client-side
// (Blob download per file — no external libs that would break the Worker CSP).

export interface RecFile {
  name: string;
  mime: string;
  content: string;
}

export interface Recommendation {
  id: string;
  site_id: string;
  week: string;
  title: string;
  body: string | null;
  impact: string | null; // high|medium|low
  kind: string | null; // aeo|content|schema|indexing
  status: RecStatus;
  files: RecFile[];
  created_at: string;
}

interface ListResp {
  ok: boolean;
  recommendations: Recommendation[];
}

interface VerifyResp {
  ok: boolean;
  verified: boolean;
  status: RecStatus;
  failed: string[];
}

interface RecsState {
  recs: Recommendation[];
  loading: boolean;
  error: string | null;
  generating: boolean;
  verifyingId: string | null;
  refresh: () => Promise<void>;
  generate: () => Promise<void>;
  setStatus: (id: string, status: RecStatus) => Promise<void>;
  verify: (id: string) => Promise<boolean>;
}

export function useRecommendations(token: string, siteId: string | null): RecsState {
  const [recs, setRecs] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!siteId) {
      setRecs([]);
      setLoading(false);
      return;
    }
    try {
      const res = await apiFetch<ListResp>(`/api/sites/${siteId}/recommendations`, token);
      setRecs(res.recommendations ?? []);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : "load_failed");
    } finally {
      setLoading(false);
    }
  }, [token, siteId]);

  useEffect(() => {
    setLoading(true);
    void refresh();
  }, [refresh]);

  const generate = useCallback(async () => {
    if (!siteId || generating) return;
    setGenerating(true);
    setError(null);
    try {
      await apiFetch(`/api/sites/${siteId}/recommend`, token, { method: "POST", body: {} });
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.code : "generate_failed");
    } finally {
      setGenerating(false);
    }
  }, [token, siteId, generating, refresh]);

  const setStatus = useCallback(
    async (id: string, status: RecStatus) => {
      // Optimistic: reflect the transition immediately, roll back on error.
      const prev = recs;
      setRecs((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      try {
        await apiFetch(`/api/recommendations/${id}`, token, { method: "PATCH", body: { status } });
      } catch (e) {
        setRecs(prev);
        setError(e instanceof ApiError ? e.code : "update_failed");
      }
    },
    [token, recs],
  );

  // Re-crawl the rec's target URL and record the verification checks. On success
  // the server flips the rec to `verified`; otherwise it stays `pending`. Returns
  // whether the change verified. This is the ONLY path that can reach `verified`.
  const verify = useCallback(
    async (id: string): Promise<boolean> => {
      if (verifyingId) return false;
      setVerifyingId(id);
      setError(null);
      try {
        const res = await apiFetch<VerifyResp>(`/api/recommendations/${id}/verify`, token, {
          method: "POST",
          body: {},
        });
        const status = res.status ?? (res.verified ? "verified" : "pending");
        setRecs((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
        return Boolean(res.verified);
      } catch (e) {
        setError(e instanceof ApiError ? e.code : "verify_failed");
        return false;
      } finally {
        setVerifyingId(null);
      }
    },
    [token, verifyingId],
  );

  return { recs, loading, error, generating, verifyingId, refresh, generate, setStatus, verify };
}

// Client-side download of a rec's file bundle: one Blob download per file. No
// zip lib (would need an external dependency / inline blob the CSP could reject).
export function downloadBundle(rec: Recommendation): void {
  const files = Array.isArray(rec.files) ? rec.files : [];
  files.forEach((f, i) => {
    // Stagger the anchor clicks so the browser doesn't drop rapid-fire downloads.
    setTimeout(() => {
      const blob = new Blob([f.content], { type: f.mime || "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }, i * 250);
  });
}
